import React, { useCallback, useEffect, useMemo, useState } from "react";
import styles from "./css/Sobre.module.css";
import { motion } from "framer-motion";

export default function Sobre() {
    const cards = useMemo(
        () => [
            {
                title: "Inovação",
                description: "Soluções digitais modernas para transformar o seu negócio.",
                icon: "💡",
            },
            {
                title: "Qualidade",
                description: "Padrão elevado de desenvolvimento com foco em excelência.",
                icon: "✅",
            },
            {
                title: "Segurança",
                description: "Aplicações confiáveis com as melhores práticas de proteção.",
                icon: "🔒",
            },
        ],
        []
    );

    // Detecta/escuta prefers-reduced-motion
    const [reduceMotion, setReduceMotion] = useState(false);
    useEffect(() => {
        if (typeof window === "undefined" || !window.matchMedia) return;
        const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
        const update = () => setReduceMotion(!!mql.matches);
        update();
        if (mql.addEventListener) mql.addEventListener("change", update);
        else mql.addListener(update);
        return () => {
            if (mql.removeEventListener) mql.removeEventListener("change", update);
            else mql.removeListener(update);
        };
    }, []);

    const getHeaderOffset = useCallback(() => {
        if (typeof window === "undefined") return 72;
        const root = document.documentElement;
        const v = getComputedStyle(root).getPropertyValue("--header-h").trim();
        const n = parseInt(v.replace("px", ""), 10);
        return (Number.isFinite(n) ? n : 72) + 8; // gap extra
    }, []);

    const focusTarget = useCallback((el) => {
        if (!el) return;
        const prev = el.getAttribute("tabindex");
        const had = prev !== null;
        if (!had) el.setAttribute("tabindex", "-1");
        el.focus({ preventScroll: true });
        if (!had) setTimeout(() => el.removeAttribute("tabindex"), 250);
    }, []);

    const scrollToContato = useCallback(
        (behavior = "smooth") => {
            if (typeof document === "undefined") return;

            const doScroll = (el) => {
                const y =
                    el.getBoundingClientRect().top +
                    window.pageYOffset -
                    getHeaderOffset();
                window.scrollTo({
                    top: y,
                    behavior: reduceMotion ? "auto" : behavior,
                });
                // atualiza hash sem poluir histórico
                if (typeof window !== "undefined" && window.history?.replaceState) {
                    window.history.replaceState(null, "", "#contato");
                }
                focusTarget(el);
            };

            const el = document.getElementById("contato");
            if (el) {
                doScroll(el);
                return;
            }

            // Fallback: ainda não montou? Grava hash e tenta de novo logo após o layout.
            if (typeof window !== "undefined" && window.history?.replaceState) {
                window.history.replaceState(null, "", "#contato");
            }
            setTimeout(() => {
                const again = document.getElementById("contato");
                if (again) doScroll(again);
            }, 100);
        },
        [focusTarget, getHeaderOffset, reduceMotion]
    );

    const handleCTAScroll = useCallback(
        (e) => {
            e.preventDefault();
            scrollToContato("smooth");
        },
        [scrollToContato]
    );

    return (
        <section id="sobre" className={styles.sobreSection} aria-labelledby="sobre-titulo">
            <div className={styles.header}>
                <h2 id="sobre-titulo">
                    Sobre a <span>HL Software</span>
                </h2>
                <p>
                    Somos especialistas em criar soluções digitais de alto impacto. Da concepção ao produto
                    final, entregamos inovação, qualidade e suporte para transformar o seu negócio.
                </p>
            </div>

            <div className={styles.cardGrid}>
                {cards.map((card, idx) => (
                    <motion.div
                        key={idx}
                        className={styles.card}
                        whileHover={
                            reduceMotion
                                ? undefined
                                : { scale: 1.05, boxShadow: "0 0 20px rgba(14,165,233,0.4)" }
                        }
                        transition={reduceMotion ? { duration: 0 } : { type: "spring", stiffness: 300 }}
                    >
                        <div className={styles.icon} aria-hidden="true">
                            {card.icon}
                        </div>
                        <h3>{card.title}</h3>
                        <p>{card.description}</p>
                    </motion.div>
                ))}
            </div>

            <div className={styles.ctaContainer}>
                <a href="#contato" onClick={handleCTAScroll} className={styles.ctaButton}>
                    Saiba Mais
                </a>
            </div>
        </section>
    );
}
